/**
 * @fileoverview One location at a glance: its picture, its name, how many plants, and
 * whether the weather reaches it.
 *
 * The card is the right way to read one bed and the wrong way to read ten.
 * This is the same bed in a third of the space; the picture and the name
 * open the bed's card in a pop-up (B157, BedQuickLook), as a plant's tile opens
 * its card. Edit bed is one tap further, from the card.
 *
 * The picture is the server's cover, as sent (its card-sized copy): the
 * bed's newest photo, else its chosen cover, else Grow's default (bug B2).
 *
 * Where: LocationGrid, the grid density, and ArchivedLocations (where a tap does nothing).
 * The Seed vault (D69) has a seed-packet icon before its name, drawn only where the server says is_vault (VaultIcon).
 *
 * API: none.
 * Words (en/es): locations.tile.open, locations.noPhoto,
 * locations.card.plants.count, locations.card.plants.count_plural,
 * locations.sheltered.short, locations.sheltered.long,
 * locations.archive.restore.
 *
 * @module LocationTile
 */
import React from 'react';
import { CloudOff, Undo2 } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { PhotoButton } from '@/shared/components/ui/PhotoButton';
import type { Location } from '@/shared/api/locations';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { VaultIcon } from '@/features/seed-orders/VaultBits';

/** One location's tile, and what pressing it does. */
interface LocationTileProps {
  /** The bed. */
  location: Location;
  /** Opens the bed's card on it (the page's pop-up); a no-op on the archive page. */
  onOpen: (location: Location) => void;
  /** Drawn faded, with a restore button, on the archive page. */
  archived?: boolean;
  /** Puts it back among the live beds (the archive page only). */
  onRestore?: (location: Location) => void;
  /** This one is being restored: its button waits. */
  isRestoring?: boolean;
}

/**
 * Only plants still growing count on the tile; the card lists the rest.
 *
 * @param location - The bed.
 */
const liveCount = (location: Location): number =>
  location.plants.filter((plant) => plant.status === 1).length;

/**
 * A location as one tile of the grid density.
 *
 * @param props - LocationTileProps.
 */
export const LocationTile: React.FC<LocationTileProps> = ({
  location, onOpen, archived, onRestore, isRestoring,
}) => {
  const { reactTranslate: t } = useTranslation();
  const [photoFailed, setPhotoFailed] = React.useState(false);
  /** The server's cover, at card size. */
  const picture = location.cover?.cardUrl ?? null;
  React.useEffect(() => setPhotoFailed(false), [picture]);
  const count = liveCount(location);

  return (
    <div className={cn(
      'group relative overflow-hidden rounded-sm border bg-card shadow-sm transition-shadow hover:shadow-md',
      archived && 'opacity-70',
    )}>
      <button
        type="button"
        onClick={() => onOpen(location)}
        aria-label={t('locations.tile.open', { name: location.name })}
        className="block w-full text-left"
      >
        <div className="relative aspect-[4/3] w-full bg-muted data-[small=true]:bg-transparent">
          {picture && !photoFailed ? (
            <FramedPhoto
              src={picture}
              alt=""
              loading="lazy"
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
              {t('locations.noPhoto')}
            </div>
          )}
        </div>
        <div className="space-y-0.5 p-2.5">
          <p className="line-clamp-2 text-sm font-medium leading-snug font-display">
            {/* D69: the seed-packet icon, only where the server says is_vault. */}
            {location.is_vault && <VaultIcon className="mr-1 inline-block h-3.5 w-3.5 align-[-2px]" />}
            {location.name}
          </p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span>{count === 1 ? t('locations.card.plants.count', { count }) : t('locations.card.plants.count_plural', { count })}</span>
            {location.sheltered && (
              <span className="inline-flex items-center gap-0.5" title={t('locations.sheltered.long')}>
                <CloudOff className="h-3 w-3" aria-hidden="true" />
                {t('locations.sheltered.short')}
              </span>
            )}
          </p>
        </div>
      </button>

      {archived && onRestore && (
        <PhotoButton
          tone="light"
          label={t('locations.archive.restore')}
          onClick={() => onRestore(location)}
          disabled={isRestoring}
          className="absolute right-2 top-2"
        >
          <Undo2 className="h-4 w-4" aria-hidden="true" />
        </PhotoButton>
      )}
    </div>
  );
};
